/**
 * Camera + microphone helpers for the web bridge.
 *
 * Android exposes these as synchronous methods (e.g. getCameraList returns
 * JSON directly). The browser APIs are all async (enumerateDevices,
 * permissions.query, getUserMedia). To keep the same Shell.* contract on
 * both platforms, we cache the latest values and refresh them eagerly:
 *
 *   - Bridge init kicks off a refresh
 *   - Each sync getter triggers a background refresh + returns the cached value
 *
 * The first call right after page load may return stale/empty results;
 * subsequent calls in the same session reflect the device's actual state.
 *
 * Module pattern (cross-platform):
 *   const list = JSON.parse(Shell.getCameraList());     // may be [] on first call
 *   if (list.length) buildPicker(list);
 *   // After requestCameraPermission grants, call getCameraList again
 *   // to pick up the device labels (browsers hide them pre-permission).
 */

const PREFS_KEY = 'shell.media.favoriteCameraId';

let cameraListCache: Array<{ id: string; facing: string; label: string }> = [];
let cameraPermissionCache = false;
let microphonePermissionCache = false;

export interface CameraEntry {
  /** deviceId — pass to getUserMedia({video:{deviceId:{exact:id}}}). */
  id: string;
  /** "Front" | "Back" | "External" | "Unknown" — best-effort guess from label. */
  facing: string;
  /** Browser-provided label (empty until user grants camera permission). */
  label: string;
}

/** Fire-and-forget refresh. Safe to call multiple times. */
export async function refreshMediaCache(): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return;

  // Permission state via the Permissions API where supported.
  if (navigator.permissions?.query) {
    try {
      const cam = await navigator.permissions.query({ name: 'camera' as PermissionName });
      cameraPermissionCache = cam.state === 'granted';
    } catch { /* not supported in some browsers (e.g. Safari) — ignore */ }
    try {
      const mic = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      microphonePermissionCache = mic.state === 'granted';
    } catch { /* same */ }
  }

  // Camera list. Browsers hide deviceId/label until permission is granted,
  // but enumerateDevices itself always succeeds.
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    cameraListCache = devices
      .filter((d) => d.kind === 'videoinput')
      .map((d) => ({
        id: d.deviceId,
        label: d.label || '',
        facing: guessFacing(d.label || ''),
      }));
  } catch {
    cameraListCache = [];
  }
}

/** Crude best-effort: browsers don't expose a facing field. */
function guessFacing(label: string): string {
  const l = label.toLowerCase();
  if (l.includes('front') || l.includes('user') || l.includes('facetime')) return 'Front';
  if (l.includes('back') || l.includes('rear') || l.includes('environment')) return 'Back';
  if (l.includes('external') || l.includes('usb') || l.includes('virtual')) return 'External';
  return 'Unknown';
}

// ─── Bridge-facing accessors (all synchronous, cache-backed) ─────────────────

export function hasCameraPermission(): boolean {
  void refreshMediaCache();
  return cameraPermissionCache;
}

export function hasMicrophonePermission(): boolean {
  void refreshMediaCache();
  return microphonePermissionCache;
}

/**
 * Triggers the browser permission prompt by briefly opening a stream, then
 * closing it immediately. Calls window[callbackName](true|false).
 */
export async function requestCameraPermission(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    stream.getTracks().forEach((t) => t.stop());
    cameraPermissionCache = true;
    // Refresh list now that labels are unlocked.
    void refreshMediaCache();
    return true;
  } catch {
    cameraPermissionCache = false;
    return false;
  }
}

export async function requestMicrophonePermission(): Promise<boolean> {
  if (!navigator.mediaDevices?.getUserMedia) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    microphonePermissionCache = true;
    void refreshMediaCache();
    return true;
  } catch {
    microphonePermissionCache = false;
    return false;
  }
}

export function getCameraList(): CameraEntry[] {
  void refreshMediaCache();
  return cameraListCache.slice();
}

export function setFavoriteCameraId(id: string): void {
  if (id) localStorage.setItem(PREFS_KEY, id);
  else localStorage.removeItem(PREFS_KEY);
}

export function getFavoriteCameraId(): string {
  return localStorage.getItem(PREFS_KEY) ?? '';
}
